import { useState, useCallback } from 'react';

// Conversion factors based on Viubux tabulador:
// Duration by pages = pages / 32.55 (hours)
// Duration by words = words / 8986.23 (hours)
// Final = average of both
const PAGES_DIVISOR = 32.55;
const WORDS_DIVISOR = 8986.23;

// Exchange rate (approximate)
const USD_EXCHANGE_RATE = 17.5;

const DEFAULT_SETTINGS = {
  words_divisor: WORDS_DIVISOR,
  pages_divisor: PAGES_DIVISOR,
  packages: [
    {
      id: 'ultra',
      name: 'Ultra',
      description: 'Audiolibro con voz clonada por IA',
      base_price: 3499,
      features: [
        'Producción de audiolibro con voz clonada IA',
        'Distribución exclusiva en Viubux',
        'Control de calidad de audio',
        'Metadatos optimizados',
        'Opción de Clonación de Voz Original (+$7,997)'
      ]
    },
    {
      id: 'essential',
      name: 'Essential',
      description: 'Solo Audiolibro y distribución dentro de Viubux',
      base_price: 6997,
      features: [
        'Producción de audiolibro profesional',
        'Distribución exclusiva en Viubux',
        'Control de calidad de audio',
        'Metadatos optimizados'
      ]
    },
    {
      id: 'platino',
      name: 'Platino',
      description: 'Videolibro y Distribución Multiplataforma',
      base_price: 13997,
      features: [
        'Todo lo incluido en Essential',
        'Videolibro con Texto resaltado o en movimiento más ilustraciones, gráficas o imágenes propias del libro',
        'Distribución multiplataforma',
        'Spotify, Apple Books, Audible',
        'Reportes de distribución'
      ]
    },
    {
      id: 'premium',
      name: 'Premium',
      description: 'Incluye materiales complementarios',
      base_price: 21997,
      features: [
        'Todo lo incluido en Platino',
        'Hasta 5 voces, 5 piezas de música y diseño sonoro simple',
        'Photoshoot',
        'Mini Masterclass o Mini Curso de hasta 25 minutos',
        'Creación de hasta 6 imágenes por hora terminada para el videolibro',
        'Kit Legal',
        'Bono de $7,500mxn en la contratación de tu evento: Presentación de Audiolibro'
      ]
    }
  ],
  volume_discounts: [
    { min_hours: 5, discount_percent: 10 },
    { min_hours: 7, discount_percent: 15 },
    { min_hours: 9, discount_percent: 20 },
    { min_hours: 11, discount_percent: 30 }
  ],
  manual_discounts: [15, 30],
  usd_exchange_rate: USD_EXCHANGE_RATE
};

export function useQuoteCalculator(initialSettings = null) {
  const [settings, setSettings] = useState(initialSettings || DEFAULT_SETTINGS);
  const [currency, setCurrency] = useState('MXN'); // 'MXN' or 'USD'

  const calculateDuration = useCallback((wordCount, pageCount) => {
    // Method 1: Duration by words (hours) = words / 8986.23
    const wordsDivisor = settings.words_divisor || WORDS_DIVISOR;
    const durationByWords = wordCount / wordsDivisor; // in hours
    
    // Method 2: Duration by pages (hours) = pages / 32.55
    const pagesDivisor = settings.pages_divisor || PAGES_DIVISOR;
    const durationByPages = pageCount / pagesDivisor; // in hours
    
    // Average of both methods
    const estimatedHours = (durationByWords + durationByPages) / 2;
    const estimatedMinutes = estimatedHours * 60;
    
    // Minimum 30 minutes
    return Math.max(estimatedMinutes, 30);
  }, [settings]);

  const calculateVolumeDiscount = useCallback((durationHours) => {
    const sortedDiscounts = [...(settings.volume_discounts || [])].sort(
      (a, b) => b.min_hours - a.min_hours
    );
    
    for (const discount of sortedDiscounts) {
      if (durationHours >= discount.min_hours) {
        return discount.discount_percent;
      }
    }
    return 0;
  }, [settings]);

  const calculateQuote = useCallback((wordCount, pageCount, packageId, manualDiscount = 0) => {
    // Find package
    const selectedPackage = settings.packages.find(p => p.id === packageId);
    if (!selectedPackage) {
      return null;
    }

    // Calculate duration
    const durationMinutes = calculateDuration(wordCount, pageCount);
    const durationHours = durationMinutes / 60;

    // Base price (always in MXN internally)
    const basePrice = selectedPackage.base_price;

    // Volume discount
    const volumeDiscountPercent = calculateVolumeDiscount(durationHours);
    const volumeDiscountAmount = basePrice * (volumeDiscountPercent / 100);

    // Price after volume discount
    const priceAfterVolume = basePrice - volumeDiscountAmount;

    // Manual discount
    const validManualDiscount = (settings.manual_discounts || []).includes(manualDiscount) 
      ? manualDiscount 
      : 0;
    const manualDiscountAmount = priceAfterVolume * (validManualDiscount / 100);

    // Final price per hour
    const finalPrice = priceAfterVolume - manualDiscountAmount;
    
    // GRAN TOTAL = precio final por hora × duración en horas
    const grandTotal = finalPrice * (durationMinutes / 60);

    return {
      package: selectedPackage,
      estimatedDurationMinutes: Math.round(durationMinutes * 100) / 100,
      estimatedDurationHours: Math.round(durationHours * 100) / 100,
      durationByWords: Math.round((wordCount / (settings.words_divisor || WORDS_DIVISOR)) * 100) / 100,
      durationByPages: Math.round((pageCount / (settings.pages_divisor || PAGES_DIVISOR)) * 100) / 100,
      basePrice,
      volumeDiscountPercent,
      volumeDiscountAmount: Math.round(volumeDiscountAmount * 100) / 100,
      manualDiscountPercent: validManualDiscount,
      manualDiscountAmount: Math.round(manualDiscountAmount * 100) / 100,
      finalPrice: Math.round(finalPrice * 100) / 100,
      totalDiscountPercent: volumeDiscountPercent + validManualDiscount,
      totalSavings: Math.round((volumeDiscountAmount + manualDiscountAmount) * 100) / 100,
      grandTotal: Math.round(grandTotal * 100) / 100,
      grandTotalBeforeDiscounts: Math.round(basePrice * durationHours * 100) / 100
    };
  }, [settings, calculateDuration, calculateVolumeDiscount]);

  const formatPrice = useCallback((price, forceCurrency = null) => {
    const curr = forceCurrency || currency;
    const exchangeRate = settings.usd_exchange_rate || USD_EXCHANGE_RATE;
    const eurExchangeRate = settings.eur_exchange_rate || 16.0;
    
    if (curr === 'USD') {
      const usdPrice = price / exchangeRate;
      return new Intl.NumberFormat('en-US', {
        style: 'currency', currency: 'USD',
        minimumFractionDigits: 0, maximumFractionDigits: 0
      }).format(usdPrice);
    }
    
    if (curr === 'EUR') {
      const eurPrice = price / eurExchangeRate;
      return new Intl.NumberFormat('de-DE', {
        style: 'currency', currency: 'EUR',
        minimumFractionDigits: 0, maximumFractionDigits: 0
      }).format(eurPrice);
    }
    
    return new Intl.NumberFormat('es-MX', {
      style: 'currency', currency: 'MXN',
      minimumFractionDigits: 0, maximumFractionDigits: 0
    }).format(price);
  }, [currency, settings]);

  const formatDuration = useCallback((minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (hours === 0) return `${mins} min`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}min`;
  }, []);

  const toggleCurrency = useCallback(() => {
    setCurrency(prev => {
      if (prev === 'MXN') return 'USD';
      if (prev === 'USD') return 'EUR';
      return 'MXN';
    });
  }, []);

  return {
    settings,
    setSettings,
    currency,
    setCurrency,
    toggleCurrency,
    calculateQuote,
    calculateDuration,
    calculateVolumeDiscount,
    formatPrice,
    formatDuration,
    packages: settings.packages || [],
    volumeDiscounts: settings.volume_discounts || [],
    manualDiscounts: settings.manual_discounts || [],
    exchangeRate: settings.usd_exchange_rate || USD_EXCHANGE_RATE
  };
}

export default useQuoteCalculator;
